import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ArrowRight, CheckCircle2, ChevronRight, Inbox, RefreshCw, Warehouse } from 'lucide-react';
import { ordersApi, storageApi, adminApi, stockApi, connectApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { PLATFORM_NAMES, timeAgo, isFailing } from '../webhookHealth';
import { ActivityList, Button, ButtonLink, Callout, EmptyState, LoadingState, RelativeTime, SetupChecklist, StatusBadge } from '../components';
import { plural } from '../utils/format';
import './Dashboard.css';

const RECENT_ORDERS = 5;
const RECENT_ACTIVITY = 5;

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// "jane.doe@shop.com" -> "Jane"
const firstName = (email) => {
  const name = (email || '').split('@')[0].split(/[._-]/)[0];
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
};

const platformName = (source) => PLATFORM_NAMES[source] || (source === 'manual' ? 'Manual' : source);

// "4 items · 7 units · WooCommerce", the same wording as the Orders page
const orderMeta = (order) => [
  plural(Number(order.item_count) || 0, 'item'),
  plural(Number(order.total_quantity) || 0, 'unit'),
  order.source && platformName(order.source),
].filter(Boolean).join(' · ');

function NextOrderCard({ nextOrder, waiting, onPick }) {
  if (!nextOrder) {
    return (
      <section className="card dash-next dash-next-clear" aria-label="Next order">
        <span className="dash-next-clear-icon" aria-hidden="true">
          <CheckCircle2 size={22} />
        </span>
        <div className="dash-next-info">
          <h2 className="dash-next-title">All caught up</h2>
          <p className="muted">No orders are waiting to be picked. New orders show up here as soon as they arrive.</p>
        </div>
        <ButtonLink to="/orders" variant="ghost" iconRight={ChevronRight} className="dash-next-action">
          View orders
        </ButtonLink>
      </section>
    );
  }

  const others = waiting - 1;
  return (
    <section className="card dash-next" aria-label="Next order">
      <div className="dash-next-info">
        <div className="stat-label">Next up · oldest first</div>
        <div className="dash-next-order">
          <h2 className="dash-next-title">
            Order <span className="mono">#{nextOrder.order_number}</span>
          </h2>
          <StatusBadge status={nextOrder.status} />
        </div>
        <p className="dash-next-meta">
          {orderMeta(nextOrder)} · received <RelativeTime value={nextOrder.created_at} />
          {others > 0 && <> · {plural(others, 'more order')} waiting</>}
        </p>
      </div>
      <Button variant="primary" size="lg" iconRight={ArrowRight} onClick={onPick} className="dash-next-action">
        Pick next order
      </Button>
    </section>
  );
}

function WebhookWarning({ failing }) {
  return (
    <Callout type="error" icon={AlertTriangle}>
      <strong>Orders may not be arriving.</strong>
      <ul className="dash-webhook-list">
        {failing.map(row => (
          <li key={row.platform}>
            {PLATFORM_NAMES[row.platform] || row.platform}: last{' '}
            {row.consecutive_failures === 1 ? 'delivery' : `${row.consecutive_failures} deliveries`} failed
            ({row.last_failure_reason}, {timeAgo(row.last_failure_at)}).
            Last successful delivery: {timeAgo(row.last_success_at)}.
          </li>
        ))}
      </ul>
      <Link to="/settings">Check webhook settings</Link>
    </Callout>
  );
}

// Connected stores whose orders or products can't be fetched (e.g. access was taken back)
function StoreProblems({ stores }) {
  return (
    <Callout type="warning" icon={AlertTriangle}>
      <strong>{stores.length === 1 ? 'A store needs' : 'Stores need'} reconnecting.</strong>
      <ul className="dash-webhook-list">
        {stores.map(store => (
          <li key={store.platform}>
            {store.name}: {store.problem.about === 'orders' ? "new orders aren't arriving" : "products aren't updating"}.{' '}
            {store.problem.message}
          </li>
        ))}
      </ul>
      <Link to="/settings">Open Settings</Link>
    </Callout>
  );
}

function StatCard({ label, value, meta, tone }) {
  return (
    <div className="card stat-card dash-stat">
      <div className="stat-label">
        <span className={`dash-stat-dot dash-stat-dot-${tone}`} aria-hidden="true" />
        {label}
      </div>
      <div className="stat-value">{value}</div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

function RecentOrders({ orders, isAdmin }) {
  return (
    <section className="card card-flush dash-panel" aria-labelledby="dash-orders-title">
      <div className="card-header dash-panel-header">
        <h2 className="card-title" id="dash-orders-title">Recent orders</h2>
        <ButtonLink to="/orders" variant="ghost" size="sm" iconRight={ChevronRight}>View all</ButtonLink>
      </div>
      {orders.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No orders yet"
          description="Orders appear here as soon as a connected store sends them."
          action={isAdmin && <ButtonLink to="/settings">Check store connections</ButtonLink>}
        />
      ) : (
        <ul className="dash-orders">
          {orders.map(order => (
            <li key={order.id}>
              <Link to={`/picking/${order.id}`} className="dash-order">
                <div className="dash-order-main">
                  <span className="dash-order-number mono">#{order.order_number}</span>
                  <span className="dash-order-meta">
                    {orderMeta(order)} · <RelativeTime value={order.created_at} />
                  </span>
                </div>
                <StatusBadge status={order.status} />
                <ChevronRight size={17} className="dash-order-chevron" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AtAGlance({ counts, systemStats, hasNextOrder }) {
  const figures = [
    { label: 'Storage units', value: counts.storageUnits },
    { label: 'SKU regions', value: counts.totalRegions },
    systemStats && { label: 'Unique SKUs', value: systemStats.uniqueSkus },
    systemStats && { label: 'Users', value: systemStats.users },
    systemStats && { label: 'Orders in 24 h', value: systemStats.ordersLast24h },
    systemStats && { label: 'Orders all time', value: systemStats.orders?.total },
  ].filter(Boolean);

  return (
    <section className="card dash-panel" aria-labelledby="dash-glance-title">
      <div className="card-header">
        <h2 className="card-title" id="dash-glance-title">At a glance</h2>
        <ButtonLink to="/storage" variant="ghost" size="sm" iconRight={ChevronRight}>Storage</ButtonLink>
      </div>
      {counts.storageUnits === 0 ? (
        <EmptyState
          icon={Warehouse}
          title="No storage units yet"
          description="Add a photo of a shelf and mark its bins so pickers can find every SKU."
          action={
            <ButtonLink to="/storage" variant={hasNextOrder ? 'secondary' : 'primary'}>
              Add your first storage unit
            </ButtonLink>
          }
        />
      ) : (
        <dl className="dash-figures">
          {figures.map(figure => (
            <div key={figure.label} className="dash-figure">
              <dt>{figure.label}</dt>
              <dd>{figure.value ?? '–'}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

// Bins whose counted stock is at or below the warning level, emptiest first
function LowStock({ rows }) {
  return (
    <section className="card card-flush dash-panel" aria-labelledby="dash-low-title">
      <div className="card-header dash-panel-header">
        <h2 className="card-title" id="dash-low-title">Running low</h2>
        <span className="badge badge-danger badge-plain">{rows.length}</span>
      </div>
      <ul className="dash-low">
        {rows.slice(0, 8).map(row => (
          <li key={`${row.region_id}:${row.sku}`}>
            <Link to={`/storage/${row.unit_id}`} className="dash-low-row">
              <span className="dash-low-name">{row.name || row.sku}</span>
              <span className="dash-low-where">{row.unit_name} · {row.label || 'bin'}</span>
              <span className={`dash-low-qty ${row.quantity === 0 ? 'is-out' : ''}`}>{row.quantity === 0 ? 'Out' : row.quantity}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [counts, setCounts] = useState({
    pendingOrders: 0,
    pickingOrders: 0,
    completedToday: 0,
    storageUnits: 0,
    totalRegions: 0,
  });
  const [activeOrders, setActiveOrders] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [failingWebhooks, setFailingWebhooks] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [storeProblems, setStoreProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [orders, active, units, sysStats, activityData, webhookStatus, low, stores] = await Promise.all([
        ordersApi.getAll(),
        ordersApi.getActive().catch(() => null),
        storageApi.getUnits(),
        adminApi.getStats().catch(() => null),
        isAdmin ? adminApi.getActivity(RECENT_ACTIVITY, 0).catch(() => ({ logs: [] })) : { logs: [] },
        isAdmin ? adminApi.getWebhookStatus().catch(() => []) : [],
        stockApi.low().catch(() => []),
        isAdmin ? connectApi.list().then(data => data.stores).catch(() => []) : [],
      ]);

      const today = new Date().toDateString();
      setCounts({
        pendingOrders: orders.filter(o => o.status === 'pending').length,
        pickingOrders: orders.filter(o => o.status === 'picking').length,
        completedToday: orders.filter(o =>
          o.status === 'completed' &&
          o.completed_at && new Date(o.completed_at).toDateString() === today
        ).length,
        storageUnits: units.length,
        totalRegions: units.reduce((sum, u) => sum + (u.region_count || 0), 0),
      });

      // /orders/active is pending + picking, oldest first. Fall back to the full list if it fails.
      setActiveOrders(active || orders
        .filter(o => o.status === 'pending' || o.status === 'picking')
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      setSystemStats(sysStats);
      setRecentOrders(orders.slice(0, RECENT_ORDERS));
      setRecentActivity(activityData.logs || []);
      setFailingWebhooks((webhookStatus || []).filter(isFailing));
      setLowStock(low || []);
      setStoreProblems(stores.filter(store => store.problem));
      setLoadError(null);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setLoadError(error.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return <LoadingState label="Loading dashboard…" />;
  }

  if (loadError) {
    return (
      <div className="container">
        <div className="card">
          <EmptyState
            icon={AlertCircle}
            title="Couldn't load the dashboard"
            description={loadError}
            action={<Button icon={RefreshCw} onClick={loadDashboard}>Try again</Button>}
          />
        </div>
      </div>
    );
  }

  const nextOrder = activeOrders[0];
  const name = firstName(user?.email);
  const oldestPending = activeOrders.find(o => o.status === 'pending');
  const toPickMeta = oldestPending
    ? <>Oldest waiting <RelativeTime value={oldestPending.created_at} /></>
    : 'Nothing waiting';

  return (
    <div className="container dashboard">
      <header className="dash-greeting">
        <p className="dash-date">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <h1 className="page-title">{greeting()}{name && `, ${name}`}</h1>
      </header>

      {isAdmin && user?.business && <SetupChecklist businessId={user.business.id} />}

      <NextOrderCard
        nextOrder={nextOrder}
        waiting={activeOrders.length}
        onPick={() => navigate(`/picking/${nextOrder.id}`)}
      />

      {failingWebhooks.length > 0 && <WebhookWarning failing={failingWebhooks} />}
      {storeProblems.length > 0 && <StoreProblems stores={storeProblems} />}

      <div className="dash-stats">
        <StatCard label="To pick" value={counts.pendingOrders} meta={toPickMeta} tone="pending" />
        <StatCard
          label="In progress"
          value={counts.pickingOrders}
          meta={counts.pickingOrders > 0 ? 'Being picked now' : 'None started'}
          tone="picking"
        />
        <StatCard
          label="Completed today"
          value={counts.completedToday}
          meta={systemStats ? `${systemStats.ordersLast24h} received in 24 h` : 'Since midnight'}
          tone="done"
        />
      </div>

      <div className="dash-columns">
        <RecentOrders orders={recentOrders} isAdmin={isAdmin} />

        <div className="dash-side">
          <AtAGlance counts={counts} systemStats={systemStats} hasNextOrder={!!nextOrder} />

          {lowStock.length > 0 && <LowStock rows={lowStock} />}

          {isAdmin && recentActivity.length > 0 && (
            <section className="card card-flush dash-panel" aria-labelledby="dash-activity-title">
              <div className="card-header dash-panel-header">
                <h2 className="card-title" id="dash-activity-title">Recent activity</h2>
                <ButtonLink to="/activity" variant="ghost" size="sm" iconRight={ChevronRight}>View all</ButtonLink>
              </div>
              <ActivityList logs={recentActivity} compact />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
